import { useEffect, useState } from 'react';
import { getImpersonation, exitImpersonation } from '../lib/impersonation.js';
import { isGuestDemo } from '../lib/demo-customer.js';

/**
 * Pinned red banner shown across the entire app whenever an admin is
 * impersonating a customer. Mounted from <Layout>. Only renders when
 * sessionStorage has an active impersonation record.
 */
export default function ImpersonationBanner() {
  const [imp, setImp] = useState(getImpersonation());
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const onChange = () => setImp(getImpersonation());
    window.addEventListener('netcom:impersonation-changed', onChange);
    window.addEventListener('storage', onChange);
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      window.removeEventListener('netcom:impersonation-changed', onChange);
      window.removeEventListener('storage', onChange);
      clearInterval(t);
    };
  }, []);

  // A public guest demo uses impersonation under the hood to drive the
  // active fleet, but the internal "Netcom Admin · viewing as · audit-logged"
  // framing must NEVER show to an external visitor — the blue DemoModeBanner
  // is their cue. Only real admins impersonating a customer see this.
  if (!imp || isGuestDemo()) return null;

  const remainingSec = Math.max(0, (imp.exp || 0) - Math.floor(now / 1000));
  const mins = Math.floor(remainingSec / 60);
  const secs = remainingSec % 60;
  const expired = remainingSec === 0;

  function handleExit() {
    exitImpersonation();
    // Hard reload so any cached customer data is wiped and the admin
    // is dropped back into their own session.
    window.location.href = '/admin/accounts';
  }

  return (
    <div
      role="alert"
      className="sticky top-0 z-50 bg-red-600 text-white shadow-lg"
      style={{ borderBottom: '3px solid #7f1d1d' }}
    >
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-5 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] sm:text-sm font-semibold leading-tight">
              <span className="opacity-80">Netcom Admin · viewing as</span>{' '}
              <span className="font-bold">{imp.businessName}</span>
              {imp.email ? <span className="opacity-80"> · {imp.email}</span> : null}
            </div>
            <div className="text-[11px] opacity-80 mono">
              {expired
                ? 'Session expired — exit to restore your admin session'
                : `Session ${mins}:${String(secs).padStart(2, '0')} remaining · every action audit-logged`}
            </div>
          </div>
        </div>
        <button
          onClick={handleExit}
          className="bg-white text-red-700 hover:bg-red-50 text-sm font-semibold px-4 py-1.5 rounded shadow-sm whitespace-nowrap"
        >
          Exit impersonation →
        </button>
      </div>
    </div>
  );
}
